// ─── VENDORED — DO NOT EDIT ─────────────────────────────────────────────────
// CONTRACT:I2-SECURITY-LABELS.1.0 — vendored decision/marking substrate (see README.md)
// Source: willackerly/TDFLite tag v0.10.0 (commit 2a1ec7cb639a7a77b8ec9e14df4cb0b139cb43aa)
// Path:   js/packages/classification-schema/src/marking.ts
// Sync:   scripts/sync-classification-schema.sh (re-vendors on a pin bump)
// Drift:  scripts/check-classification-schema-drift.sh (this header + the
//         "@tdflite/contracts" specifier rewrite + the de-triggered upstream
//         contract line are NORMALIZED; any OTHER divergence FAILS the gate).
// ─── END VENDORED HEADER ─────────────────────────────────────────────────────
// upstream-contract F1-CLASSIFICATION-SCHEMA.0.1 (TDFLite; see vendored header)
/** marking — the CAPCO MARKING-STRING builder (ZTACO F1 Slice 2, ADR-0013).
 *
 *  Slice 1 shipped the DECISION substrate (the read-down evaluator + the one lattice +
 *  the marking-shape taxonomy). Slice 2 adds the inverse-facing primitive: turn a set of
 *  SecurityLabels INTO the DoD/IC-compliant overall banner string a screen paints —
 *  labels → the CAPCO banner line.
 *
 *  SINGLE-SOURCED from tak-tdf apps/cop/src/components/dod-marking.ts (the canonical
 *  builder — bannerLine + its ordering helpers). The COLOR / palette / tier helpers
 *  (bannerColor / bannerTextColor / bannerTier) are Tier-2 PRESENTATION and deliberately
 *  NOT lifted here — Slice 2 is the STRING only. The string composition below is byte-
 *  identical to the canonical so the (net-new) Go twin can be a DIRECT byte-for-byte port
 *  and the go-tak-server 3rd engine's round-trip (parseBanner(bannerLine(labels)) ==
 *  deriveBoundFqns(labels)) holds by construction.
 *
 *  ADAPTATION (dod-marking.ts reads a `HighWater`; we read a `SecurityLabels`):
 *    - HighWater.missions  → SecurityLabels.mission (the SCI-carrying facet)
 *    - HighWater.caveats   → SecurityLabels.caveats (dissem + SCI/AEA/FGI/CUI overflow)
 *    - HighWater.releasability → SecurityLabels.releasability
 *  Two SecurityLabels-native touches, documented at their call sites:
 *    - an EMPTY / absent classification normalizes to "UNCLASSIFIED" (a HighWater always
 *      arrives pre-defaulted by the COP aggregator; a bare SecurityLabels may not);
 *    - `originator_control: true` folds into the caveat stream as an ORCON token, since
 *      the two representations are equivalent upstream (ORCON-in-caveats ⇔
 *      originator_control:true) — so both paint the banner identically.
 *
 *  Banner-line segment order (Fuller CAPCO — ARC 1), "//"-separated:
 *    CLASSIFICATION // SCI // AEA // FGI // DISSEMINATION (+ RELEASABLE TO) // CUI
 */

import type { SecurityLabels } from "./labels";
import { buildAttrFqn, SEED_BUNDLE } from "./evaluate";

// ---------------------------------------------------------------------------
// Constants — single-sourced from tak-tdf dod-marking.ts. Keep in lockstep; a
// drift here silently changes a compliance-bearing banner string.
// ---------------------------------------------------------------------------

/** Full-caps SPELLED-OUT classification label keyed by canonical classification. The one
 *  spell-out that matters: TOP_SECRET → "TOP SECRET". The presentation-only TS_SCI tier
 *  from dod-marking.ts is intentionally excluded (it is a COLOR tier, not a classification
 *  on the lattice — the SCI compartment paints via the SCI segment, never the class slug). */
const CLASS_LABEL: Record<string, string> = {
  UNCLASSIFIED: "UNCLASSIFIED",
  CUI: "CUI",
  CONFIDENTIAL: "CONFIDENTIAL",
  SECRET: "SECRET",
  TOP_SECRET: "TOP SECRET",
};

/** Canonical SCI markings — the STRICT WHITELIST the SCI banner segment renders. A token
 *  that is not one of these — a program/operation NICKNAME ("STEEL_HORIZON", "HIGH_MAST")
 *  or a chat-room name — is NEVER an SCI marking and must never leak into the CAPCO line.
 *  Union of the bundle's sci_control_system (SI/TK/HCS) + sci_compartment (SI-G/…) vocab. */
export const SCI_SET: ReadonlySet<string> = new Set(["SI", "TK", "HCS", "SI-G", "TK-RUFF", "SI-GAMMA", "ECI"]);

/** Canonical dissemination-control display order (the CAPCO dissem segment). Lower index =
 *  earlier. Controls not listed here sort AFTER the known ones, alphabetically, so the
 *  segment stays deterministic. `REL_TO` is NOT here — releasability is its own axis,
 *  rendered as "RELEASABLE TO …". */
export const DISSEM_ORDER: readonly string[] = [
  "RSEN",
  "ORCON",
  "IMCON",
  "NOFORN",
  "PROPIN",
  "RELIDO",
  "FOUO",
  "LIMDIS",
  "DISPLAY ONLY",
  "FISA",
];

/** AEA (Atomic Energy Act / DOE) markings — their own CAPCO segment, after SCI/SAP, in
 *  CAPCO precedence order (RD → FRD → DCNI → CNWDI). */
export const AEA_ORDER: readonly string[] = ["RD", "FRD", "DCNI", "CNWDI"];
const AEA_SET: ReadonlySet<string> = new Set(AEA_ORDER);

/** CUI specified-category markings — they trail in the "OTHER" segment (after dissem). */
export const CUI_CATEGORY_SET: ReadonlySet<string> = new Set(["SP-CTI", "SP-PRVCY", "SP-LEI"]);

/** Classified-only dissemination controls (ICD 710 / DoDM 5200.01 Vol 2): authorized only
 *  for CLASSIFIED national intelligence, so they must NEVER render on a plain UNCLASSIFIED
 *  banner (e.g. "UNCLASSIFIED//NOFORN" is non-compliant). Defense-in-depth display guard;
 *  the PDP already fail-closes the contradiction upstream. PROPIN/FOUO are intentionally
 *  excluded — they are not classified-only. */
export const CLASSIFIED_ONLY_DISSEM: ReadonlySet<string> = new Set(["NOFORN", "ORCON", "IMCON", "RELIDO"]);

/** The SPELLED-OUT releasability prefix CAPCO permits in a banner line. The long form
 *  "RELEASABLE TO" — never the "REL:" or bare "REL TO" short forms — so the banner reads
 *  in plain compliant English. */
export const RELEASABLE_TO_PREFIX = "RELEASABLE TO";

/** Scenario / non-marking tags that must never appear in a real banner line. */
const NON_MARKINGS: ReadonlySet<string> = new Set(["DEMO", "TEST", "EXERCISE", "SAMPLE", ""]);

// The bundle attribute names deriveBoundFqns binds each routed token under. Named so a
// typo can't silently misroute a marking to the wrong attribute FQN.
const CLASSIFICATION_ATTR = "classification";
const SCI_CONTROL_SYSTEM_ATTR = "sci_control_system";
const SCI_COMPARTMENT_ATTR = "sci_compartment";
const HANDLING_CAVEAT_ATTR = "handling_caveat";
const RELEASABILITY_ATTR = "releasability";
const ATOMIC_ENERGY_ATTR = "atomic_energy";
const FGI_CONSTRAINT_ATTR = "fgi_constraint";
const CUI_CATEGORY_ATTR = "cui_category";

/** value(UPPER) → SCI attribute name, single-sourced from the SEED_BUNDLE so SI routes to
 *  sci_control_system and SI-G to sci_compartment (the two vocabularies are disjoint, and
 *  their union is exactly SCI_SET — so every whitelisted SCI token resolves). Built once. */
const SCI_ATTR: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const a of SEED_BUNDLE.attributes) {
    if (a.name === SCI_CONTROL_SYSTEM_ATTR || a.name === SCI_COMPARTMENT_ATTR) {
      for (const v of a.values) m.set(v.toUpperCase(), a.name);
    }
  }
  return m;
})();

// ---------------------------------------------------------------------------
// Token helpers — single-sourced from dod-marking.ts.
// ---------------------------------------------------------------------------

/** A token is an FGI (Foreign Government Information) marking iff it is `FGI-<gov>`. */
const isFgi = (t: string): boolean => /^FGI-/.test(t);

/** REL_TO is a concept error AS a dissem caveat: it is the releasability axis, not a
 *  handling caveat. Drop a bare REL_TO token from the dissem segment entirely. */
const isRelToCaveat = (t: string): boolean => /^REL[:\s_]/i.test(t) || t === "RELTO";

/** True iff the token is (case-insensitively) NOFORN. */
const isNoforn = (c: string): boolean => c.trim().toUpperCase() === "NOFORN";

/** Normalize a raw marking token for CAPCO routing: trim, strip a non-CAPCO field-name
 *  PREFIX some datasets prepend ("SCI:SI" → "SI", "COMPARTMENT:SI-G" → "SI-G", "SAP:BRAVO"
 *  → "BRAVO"), upper-case. The banner shows the bare marking, never the field-name label. */
function normMarking(raw: string): string {
  return raw
    .trim()
    .replace(/^(?:SCI|SCI[_-]?CONTROL|COMPARTMENTS?|SAP|CONTROL)\s*:\s*/i, "")
    .trim()
    .toUpperCase();
}

/** Full-caps spelled-out classification label (e.g. "TOP_SECRET" → "TOP SECRET"). An
 *  unrecognized classification passes through unchanged (fail-open on the LABEL only —
 *  the classification still binds fail-closed via its FQN in deriveBoundFqns). */
export function classificationLabel(classification: string): string {
  return CLASS_LABEL[classification] ?? classification;
}

/** Order REL-TO country codes: "USA" first, then trigraphs (3 chars) ascending, then
 *  tetragraphs / coalitions (4+ chars, e.g. FVEY/NATO/ISAF) ascending. Dedupes; USA is
 *  ALWAYS present and leading when a REL TO segment is emitted (injected if the caller
 *  omitted it). Tetragraphs are detected by LENGTH (not a fixed coalition whitelist) so an
 *  unmodeled coalition still sorts correctly. */
function orderRelTo(countries: string[]): string[] {
  const set = new Set(countries.map((c) => c.trim().toUpperCase()).filter((c) => c && c !== "USA"));
  const tri = [...set].filter((c) => c.length === 3).sort();
  const tetra = [...set].filter((c) => c.length !== 3).sort();
  return ["USA", ...tri, ...tetra];
}

/** Order dissemination controls by the canonical CAPCO sequence; unknown controls trail the
 *  known ones alphabetically. Returns deduped, upper-cased tokens. EXCLUDES the tokens that
 *  belong to OTHER CAPCO segments (SCI / AEA / FGI / CUI category) and DROPS the REL_TO
 *  concept error — those are routed elsewhere. */
function orderDissem(caveats: string[]): string[] {
  const seen = new Set<string>();
  const known: string[] = [];
  const unknown: string[] = [];
  for (const raw of caveats) {
    const c = normMarking(raw);
    if (!c || NON_MARKINGS.has(c) || seen.has(c)) continue;
    // Not a dissem control — handled by its own segment (SCI/AEA/FGI/CUI) or dropped (REL TO).
    if (SCI_SET.has(c) || AEA_SET.has(c) || CUI_CATEGORY_SET.has(c) || isFgi(c) || isRelToCaveat(c)) continue;
    seen.add(c);
    if (DISSEM_ORDER.includes(c)) known.push(c);
    else unknown.push(c);
  }
  known.sort((a, b) => DISSEM_ORDER.indexOf(a) - DISSEM_ORDER.indexOf(b));
  unknown.sort();
  return [...known, ...unknown];
}

/** Collect, dedupe, upper-case + filter non-markings from a token list. */
function cleanTokens(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list) {
    const t = normMarking(raw);
    if (!t || NON_MARKINGS.has(t) || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Routing — the ONE place the CAPCO segments are computed. bannerLine renders it to a
// string; deriveBoundFqns maps it to FQNs. Sharing the routing is what makes the round-trip
// (parseBanner(bannerLine(labels)) == deriveBoundFqns(labels)) hold by construction: both
// faces are derived from the identical routed segments.
// ---------------------------------------------------------------------------

interface RoutedMarking {
  /** Canonical classification token (e.g. "TOP_SECRET"); empty/absent → "UNCLASSIFIED". */
  classification: string;
  /** Whitelisted SCI markings, deduped + sorted (nicknames dropped). */
  sci: string[];
  /** AEA markings in CAPCO precedence order. */
  aea: string[];
  /** FGI-<gov> markings, deduped + sorted. */
  fgi: string[];
  /** Dissemination controls in canonical order, AFTER the UNCLASSIFIED classified-only drop. */
  dissem: string[];
  /** REL-TO countries (USA-first order) — [] when NOFORN dominates or none are present. */
  rel: string[];
  /** CUI categories, deduped + sorted. */
  cui: string[];
}

/** Route a SecurityLabels set into its CAPCO segments. Enforces every decision-bearing
 *  rule: the SCI whitelist (nickname-drop), CAPCO precedence, the classified-only-dissem
 *  guard on a plain UNCLASSIFIED banner, and NOFORN-dominates (drops RELEASABLE TO). */
function route(labels: SecurityLabels): RoutedMarking {
  // SecurityLabels-native normalization: a bare label set may carry an empty classification;
  // a compliant banner is never blank — default to UNCLASSIFIED (the COP feeds a HighWater
  // that is already pre-defaulted, so this only bites the direct-SecurityLabels path).
  const classification = (labels.classification ?? "").trim() || "UNCLASSIFIED";

  // ORCON arrives either as a caveat token OR via the originator_control boolean; fold the
  // boolean in so both equivalent representations paint identically (dedup is downstream).
  const caveatsIn = [...(labels.caveats ?? [])];
  if (labels.originator_control === true) caveatsIn.push("ORCON");

  // AEA / FGI / CUI / SCI route from BOTH token streams (mission + caveats); dissem routes
  // from caveats only, releasability from its own axis.
  const allToks = [...cleanTokens(labels.mission ?? []), ...cleanTokens(caveatsIn)];

  const aea = AEA_ORDER.filter((v) => allToks.includes(v)); // CAPCO precedence order
  const fgi = [...new Set(allToks.filter(isFgi))].sort();
  const cui = [...new Set(allToks.filter((t) => CUI_CATEGORY_SET.has(t)))].sort();
  // SCI segment — the strict whitelist. A non-recognized token (program nickname / room
  // name) is DROPPED here, never rendered in the CAPCO line. (deduped, sorted)
  const sci = [...new Set(allToks.filter((t) => SCI_SET.has(t)))].sort();

  // Dissemination — canonical order, then the defense-in-depth guard: a classified-only
  // control must NEVER paint on a plain UNCLASSIFIED banner. (CUI is NOT plain UNCLASSIFIED,
  // so CUI//NOFORN is untouched — CUI is its own classification on the lattice.)
  const dissem0 = orderDissem(caveatsIn);
  const dissem =
    classification === "UNCLASSIFIED" ? dissem0.filter((c) => !CLASSIFIED_ONLY_DISSEM.has(c)) : dissem0;
  const noforn = dissem.some(isNoforn);

  // NOFORN and RELEASABLE TO are mutually exclusive; NOFORN dominates the aggregate and
  // DROPS releasability. When REL is emitted, USA leads (orderRelTo injects it if omitted).
  const relIn = (labels.releasability ?? []).filter((c) => {
    const u = c.trim().toUpperCase();
    return u !== "" && !NON_MARKINGS.has(u);
  });
  const rel = !noforn && relIn.length > 0 ? orderRelTo(relIn) : [];

  return { classification, sci, aea, fgi, dissem, rel, cui };
}

// ---------------------------------------------------------------------------
// The two public faces of the routed marking.
// ---------------------------------------------------------------------------

/** Build the DoD/IC-compliant overall BANNER LINE from a SecurityLabels set, in the Fuller
 *  CAPCO segment order:
 *
 *    CLASSIFICATION // SCI // AEA // FGI // DISSEMINATION (+ RELEASABLE TO) // CUI
 *
 *  Enforces NOFORN-vs-REL exclusivity (NOFORN dominates), the SCI whitelist (nickname-drop),
 *  the classified-only-dissem guard on UNCLASSIFIED, and canonical ordering throughout.
 *  Empty / unmarked labels → "UNCLASSIFIED". */
export function bannerLine(labels: SecurityLabels): string {
  const r = route(labels);
  const segments: string[] = [classificationLabel(r.classification)];

  if (r.sci.length > 0) segments.push(r.sci.join("/"));
  if (r.aea.length > 0) segments.push(r.aea.join("/"));
  if (r.fgi.length > 0) segments.push(r.fgi.join("/"));

  // Dissemination segment: canonical-order controls, then RELEASABLE TO appended (only when
  // nothing is NOFORN — enforced by route()), spelled out per the banner long form.
  const dissemTokens: string[] = [...r.dissem];
  if (r.rel.length > 0) dissemTokens.push(`${RELEASABLE_TO_PREFIX} ${r.rel.join(", ")}`);
  if (dissemTokens.length > 0) segments.push(dissemTokens.join("/"));

  // CUI / OTHER segment trails dissemination per CAPCO.
  if (r.cui.length > 0) segments.push(r.cui.join("/"));

  return segments.join("//");
}

/** Derive the bound attribute-FQN SET the marking corresponds to — the round-trip contract
 *  target (parseBanner(bannerLine(labels)) == deriveBoundFqns(labels), run cross-engine by
 *  go-tak-server). Canonical https:// FQNs via buildAttrFqn, sorted + deduped.
 *
 *  Derived from the SAME routed segments bannerLine renders, so the FQN set is exactly what
 *  the banner string encodes — every guard (SCI whitelist, classified-only-on-UNCLASSIFIED
 *  drop, NOFORN-drops-REL, USA-injection) applies identically. Classification always binds
 *  (the banner always leads with it); each SCI token binds to its own attribute
 *  (sci_control_system vs sci_compartment) via the bundle-sourced SCI index. */
export function deriveBoundFqns(labels: SecurityLabels): string[] {
  const r = route(labels);
  const ns = SEED_BUNDLE.namespace;
  const fqns = new Set<string>();

  fqns.add(buildAttrFqn(ns, CLASSIFICATION_ATTR, r.classification));
  for (const t of r.sci) fqns.add(buildAttrFqn(ns, SCI_ATTR.get(t) ?? SCI_CONTROL_SYSTEM_ATTR, t));
  for (const t of r.aea) fqns.add(buildAttrFqn(ns, ATOMIC_ENERGY_ATTR, t));
  for (const t of r.fgi) fqns.add(buildAttrFqn(ns, FGI_CONSTRAINT_ATTR, t));
  for (const t of r.dissem) fqns.add(buildAttrFqn(ns, HANDLING_CAVEAT_ATTR, t));
  for (const t of r.rel) fqns.add(buildAttrFqn(ns, RELEASABILITY_ATTR, t));
  for (const t of r.cui) fqns.add(buildAttrFqn(ns, CUI_CATEGORY_ATTR, t));

  return [...fqns].sort();
}
